/**
 * OpenAI Service
 * Production-ready AI service with rate limiting, retries, guardrails, and error handling.
 * All API calls are proxied through a Cloudflare Pages Function so the
 * OpenAI API key is never bundled into client-side JavaScript.
 */

import type { InvoiceData } from '../types';
import { logger } from './logger';
import { 
  validateInput, 
  validateOutput, 
  enhancePrompt, 
  getGuardedSystemPrompt,
  type GuardrailsConfig 
} from './aiGuardrails';

// Server-side proxy endpoint (Cloudflare Pages Function at /api/ai)
const AI_PROXY_URL = '/api/ai';

// Rate limiting state
const rateLimitState = {
  requestCount: 0,
  windowStart: Date.now(),
  maxRequestsPerMinute: 60,
};

/**
 * AI features are always potentially available — the server-side proxy
 * holds the API key. If the key is missing server-side the proxy returns 503
 * and the UI shows an appropriate error.
 */
export const isAIAvailable = (): boolean => {
  return true;
};

/**
 * Simple client-side rate limiter
 */
const checkRateLimit = (): boolean => {
  const now = Date.now();
  const windowDuration = 60000; // 1 minute

  // Reset window if expired
  if (now - rateLimitState.windowStart > windowDuration) {
    rateLimitState.requestCount = 0;
    rateLimitState.windowStart = now;
  }

  if (rateLimitState.requestCount >= rateLimitState.maxRequestsPerMinute) {
    return false;
  }

  rateLimitState.requestCount++;
  return true;
};

/**
 * Sleep utility for retry backoff
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Validates connectivity to the AI proxy
 */
export const testConnectivity = async (): Promise<boolean> => {
  try {
    logger.startTimer('ai_connectivity_test');
    const response = await fetch(AI_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'test' }),
    });
    logger.endTimer('ai_connectivity_test');
    if (!response.ok) return false;
    const data = await response.json();
    return data.success === true;
  } catch (error) {
    logger.warn('AI Connectivity Test Failed', { error });
    return false;
  }
};

/**
 * Extracts structured invoice data from unstructured user input.
 * Requests are proxied through the server to keep API keys secure.
 * Client-side guardrails, retry logic, and rate limiting remain here.
 */
export const generateInvoiceFromText = async (
  promptText: string,
  maxRetries = 3,
  guardrailsConfig?: GuardrailsConfig
): Promise<Partial<InvoiceData>> => {
  // ========== GUARDRAIL: Input Validation ==========
  const inputValidation = validateInput(promptText, guardrailsConfig);
  
  if (!inputValidation.isValid) {
    logger.warn('Input rejected by guardrails', { 
      reason: inputValidation.rejectionReason,
      confidence: inputValidation.confidence 
    });
    throw new Error(inputValidation.rejectionReason || 'INVALID_INPUT');
  }
  
  // Enhance prompt for better results with rough inputs
  const enhancedPrompt = enhancePrompt(inputValidation.sanitizedInput);
  
  logger.info('Input passed guardrails', { 
    confidence: inputValidation.confidence,
    originalLength: promptText.length,
    sanitizedLength: inputValidation.sanitizedInput.length 
  });

  // Check rate limit
  if (!checkRateLimit()) {
    throw new Error('RATE_LIMIT_EXCEEDED');
  }

  // Use guarded system prompt
  const systemPrompt = getGuardedSystemPrompt();

  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.startTimer(`invoice_generation_attempt_${attempt}`);

      const response = await fetch(AI_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: enhancedPrompt },
          ],
          max_tokens: 1500,
          temperature: 0.3,
        }),
      });

      logger.endTimer(`invoice_generation_attempt_${attempt}`);

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Proxy error (${response.status}): ${errorBody}`);
      }

      const proxyData = await response.json();

      if (proxyData.error) {
        throw new Error(proxyData.error);
      }

      const text = proxyData.choices?.[0]?.message?.content;
      if (!text) {
        throw new Error('Empty response from AI');
      }

      const rawData = JSON.parse(text);
      
      // ========== GUARDRAIL: Check for AI rejection ==========
      if (rawData.error === 'NOT_INVOICE_RELATED') {
        throw new Error(rawData.message || 'Please provide invoice-related details');
      }

      // ========== GUARDRAIL: Output Validation ==========
      const outputValidation = validateOutput(rawData);
      
      if (!outputValidation.isValid) {
        logger.warn('AI output failed validation', { warnings: outputValidation.warnings });
        throw new Error('AI response could not be validated as invoice data');
      }
      
      // Log any corrections made
      if (outputValidation.corrections.length > 0) {
        logger.info('Output corrections applied', { corrections: outputValidation.corrections });
      }
      if (outputValidation.warnings.length > 0) {
        logger.debug('Output warnings', { warnings: outputValidation.warnings });
      }

      const data = outputValidation.sanitizedData;

      // Ensure items array exists with proper structure
      if (!data.items || data.items.length === 0) {
        data.items = [
          {
            id: crypto.randomUUID(),
            description: 'Service',
            quantity: 1,
            rate: 0,
            tax: 0,
          },
        ];
      }

      logger.info('Invoice generated successfully with guardrails', {
        itemCount: data.items?.length,
        hasRecipient: !!data.recipientName,
        inputConfidence: inputValidation.confidence,
        outputCorrections: outputValidation.corrections.length,
      });

      return data;
    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error(String(error));
      logger.warn(`AI generation attempt ${attempt} failed`, { error: lastError.message });

      // Check if it's a rate limit error from OpenAI
      if (lastError.message.includes('429') || lastError.message.includes('rate_limit')) {
        // Exponential backoff
        await sleep(Math.pow(2, attempt) * 1000);
        continue;
      }

      // Don't retry on certain errors
      if (
        lastError.message.includes('API_KEY') ||
        lastError.message.includes('401') ||
        lastError.message.includes('invalid_api_key') ||
        lastError.message.includes('503')
      ) {
        throw lastError;
      }

      // Wait before retry
      if (attempt < maxRetries) {
        await sleep(1000 * attempt);
      }
    }
  }

  throw lastError || new Error('AI generation failed after retries');
};

/**
 * Get current rate limit status
 */
export const getRateLimitStatus = () => ({
  remaining: rateLimitState.maxRequestsPerMinute - rateLimitState.requestCount,
  resetIn: Math.max(0, 60000 - (Date.now() - rateLimitState.windowStart)),
});

// Re-export guardrails for external use
export { validateInput, validateOutput, enhancePrompt } from './aiGuardrails';
export type { ValidationResult, GuardrailsConfig } from './aiGuardrails';
